import { createMMKV } from 'react-native-mmkv';

// ─────────────────────────────────────────────────────────────────────────────

export function getActiveGroupOrderId() {
  const reference = getGroupOrderReference();

  return reference?.status === ACTIVE_STATUS ? reference.orderId : undefined;
}

export function storeActiveGroupOrderId(orderId: string) {
  const reference = getGroupOrderReference();
  const hasTrackedOrder = reference?.orderId === orderId;

  if (hasTrackedOrder) return;

  storeGroupOrderReference({ orderId, status: ACTIVE_STATUS });
}

export function checkIfGroupOrderDismissed(orderId: string) {
  const reference = getGroupOrderReference();

  return reference?.orderId === orderId && reference.status === DISMISSED_STATUS;
}

export function storeDismissedGroupOrderId(orderId: string) {
  storeGroupOrderReference({ orderId, status: DISMISSED_STATUS });
}

export function clearGroupOrderReference() {
  GROUP_ORDER_STORAGE.remove(GROUP_ORDER_REFERENCE_KEY);
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getGroupOrderReference() {
  const storedReference = GROUP_ORDER_STORAGE.getString(GROUP_ORDER_REFERENCE_KEY);

  if (!storedReference) return;

  try {
    const reference: unknown = JSON.parse(storedReference);

    return checkIfGroupOrderReference(reference) ? reference : undefined;
  } catch {
    return;
  }
}

function storeGroupOrderReference(reference: GroupOrderReference) {
  GROUP_ORDER_STORAGE.set(GROUP_ORDER_REFERENCE_KEY, JSON.stringify(reference));
}

function checkIfGroupOrderReference(reference: unknown): reference is GroupOrderReference {
  if (!reference || typeof reference !== 'object') return false;

  const referenceRecord = reference as Record<string, unknown>;
  const isKnownStatus =
    referenceRecord.status === ACTIVE_STATUS || referenceRecord.status === DISMISSED_STATUS;

  return typeof referenceRecord.orderId === 'string' && isKnownStatus;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const GROUP_ORDER_STORAGE = createMMKV({ id: 'sg.group-order' });
const GROUP_ORDER_REFERENCE_KEY = 'group-order-reference';
const ACTIVE_STATUS = 'active';
const DISMISSED_STATUS = 'dismissed';

// ─── Types ───────────────────────────────────────────────────────────────────

type GroupOrderReference = {
  orderId: string;
  status: typeof ACTIVE_STATUS | typeof DISMISSED_STATUS;
};
